import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  chartTypes,
  disaggregations,
  mapTypes,
} from '../common/utils/enum'
import { NONE, ONLY_SHOW_DEFAULT } from '../common/utils/constants'
import { getSuppressedCountries } from '../common/utils/helper'
import fetchData from '../service/apiService'
import worldBoundariesData from './charts/map/mapData/World-including-disputed.json'
import regionBoundariesData from './charts/map/mapData/wb-regions.geojson'
import continentBoundariesData from './charts/map/mapData/wb-continent.geojson'

const ongoingRequests = {}

const getBoundaryFallbackData = (boundaryType) => {
  if (boundaryType === mapTypes.Region) {
    return regionBoundariesData
  }

  if (boundaryType === mapTypes.Continent) {
    return continentBoundariesData
  }

  if (boundaryType === mapTypes.Country) {
    return worldBoundariesData
  }

  return null
}

export const fetchAndSetBoundaries = createAsyncThunk(
  'indicatorProfile/fetchAndSetBoundaries',
  async ({ endpoint, boundaryType, headers = {} }, { getState, dispatch }) => {
    const key = `${boundaryType}Boundaries`

    if (ongoingRequests[key]) {
      return ongoingRequests[key].then(() => getState().indicatorProfile[key])
    }

    const existingData = getState().indicatorProfile[key]
    if (existingData && existingData.features && existingData.features.length) {
      return existingData
    }

    try {
      const requestPromise = fetchData(endpoint, 'GET', null, headers)
      ongoingRequests[key] = requestPromise

      const data = await requestPromise
     dispatch(setBoundaries({ boundaryType, data }))
      return data
    } catch (error) {
      console.error('Error fetching boundaries:', error)
      const fallbackData = getBoundaryFallbackData(boundaryType)

      if (fallbackData) {
        dispatch(setBoundaries({ boundaryType, data: fallbackData }))
        return fallbackData
      }

      throw error
    } finally {
      delete ongoingRequests[key]
    }
  }
)

const indicatorProfileSlice = createSlice({
  name: 'indicatorProfile',
  initialState: {
    indicatorCode: '',
    defaultCountryCode: '',
    indicatorMetadata: null,
    isIndicatorMetadataCompleted: false,
    disaggregationData: null,
    secondDisaggregationData: [],
    disaggregationCountries: [],
    isDisaggregationCompleted: false,
    selectedFilters: {},
    selectedCountries: [],
    selectedCountriesForBar: ['all'],
    indicatorMinYear: 0,
    indicatorMaxYear: 0,
    selectedMinYear: 0,
    selectedMaxYear: 0,
    recentYearSelection: true,
    selectedSingleYear: 0,
    indicatorData: [],
    filteredIndicatorData: [],
    selectedComparison: NONE,
    defaultFrequency: '',
    defaultUnitMeasure: '',
    defaultSecondUnitMeasure: '',
    defaultSecondIndicator: '',
    selectedSecondIndicator: '',
    secondIndicatorData: [],
    isLoading: false,
    isSecondIndicatorLoading: false,
    dataTableData: [],
    selectedUnitMeasure: '',
    selectedSecondUnitMeasure: '',
    selectedFrequency: '',
    selectedOnlyShow: {
      [chartTypes.Map]: ONLY_SHOW_DEFAULT,
      [chartTypes.Correlation]: ONLY_SHOW_DEFAULT,
    },
    selectedAverage: {
      [chartTypes.Trend]: NONE,
      [chartTypes.Correlation]: NONE,
      [chartTypes.Bar]: NONE,
      [chartTypes.DataTable]: NONE,
      [chartTypes.StackedBar]: NONE,
      [chartTypes.Pie]: NONE,
    },
    selectedAverages: [],
    aggregateData: [],
    filteredAggregateData: [],
    isTrendPopupShown: false,
    isSelectedFiltersUpdated: false,
    isAggregateDataLoading: false,
    unitMeasureName: '',
    countryHierarchy: {},
    indicatorDataForCustomReport: {},
    indicatorSpecificRegions: [],
    dimensionsData: null,
    changedFilterName: null,
    isDimensionsLoading: false,
    selectedDimensionsList: [],
    refGroupings: null,
    boundaryType: null,
    availableBoundaryTypes: [],
    countryBoundaries: null,
    regionBoundaries: null,
    continentBoundaries: null,
    countryListForIndicator: [],
    isCountryListLoaded: false,
  },
  reducers: {
    setIndicatorCode: (state, action) => {
      state.indicatorCode = action.payload
    },
    setDefaultCountryCode: (state, action) => {
      state.defaultCountryCode = action.payload
    },
    setIndicatorMetadata: (state, action) => {
      state.indicatorMetadata = action.payload
    },
    setIsIndicatorMetadataCompleted: (state, action) => {
      state.isIndicatorMetadataCompleted = action.payload
    },
    setDisaggregationData: (state, action) => {
      const { disaggregation, visualization, refGroupingsCodelist } =
        action.payload
      state.disaggregationData = disaggregation
      let countries =
        disaggregation?.dimensions && disaggregation?.dimensions.length > 0
          ? disaggregation?.dimensions.find(
              (item) => item.field_name === disaggregations.RefArea
            )?.field_value
          : []
      const suppressedCountries = visualization?.suppression
      // const suppressedCountries = 'AUT'
      countries = countries.filter(
        (item) =>
          !getSuppressedCountries(suppressedCountries).includes(item.code)
      )
      state.disaggregationCountries =
        countries && countries.length > 0 ? countries : []
      state.isDisaggregationCompleted = true

      const refGroupingIds = new Set(
        refGroupingsCodelist?.map((item) => item.id)
      )
      const countryList = state.disaggregationCountries.filter(
        (country) => !refGroupingIds.has(country.code)
      )
      state.countryListForIndicator =
        countryList.map((country) => ({
          id: country.code,
          name: country.description,
        })) || []
      state.isCountryListLoaded = true
    },
    setSecondDisaggregationData: (state, action) => {
      state.secondDisaggregationData = action.payload
    },
    setInitialFilters: (state, action) => {
      state.selectedFilters = action.payload
    },
    seIsSelectedFiltersUpdated: (state, action) => {
      state.isSelectedFiltersUpdated = action.payload
    },
    setSelectedFilters: (state, action) => {
      const { name, value, selectedComparison } = action.payload

      if (name === 'init') {
        state.selectedFilters = value
      } else {
        if (
          selectedComparison === NONE ||
          name === selectedComparison ||
          selectedComparison === ''
        ) {
          state.selectedFilters = {
            ...state.selectedFilters,
            [name]: value,
          }
        } else {
          state.selectedFilters = {
            ...state.selectedFilters,
            [name]:
              value.split(',').length > 1
                ? value
                : state.selectedFilters[name]
                  ? state.selectedFilters[name].split(',').includes(value)
                    ? state.selectedFilters[name]
                        .split(',')
                        .filter((v) => v !== value)
                        .join(',')
                    : // : [...state.selectedFilters[name].split(","), value].join(",")
                      value
                  : value,
          }
        }
      }
      state.isSelectedFiltersUpdated = true
    },
    resetSelectedCountries: (state, action) => {
      state.selectedCountries = []
    },
    resetSelectedAverage: (state, action) => {
      state.selectedAverage = {
        [chartTypes.Trend]: action.payload,
        [chartTypes.Bar]: action.payload,
        [chartTypes.Correlation]: action.payload,
        [chartTypes.DataTable]: action.payload,
      }
    },
    setSelectedCountries: (state, action) => {
      state.selectedCountries = action.payload
    },
    setSelectedCountriesForBar: (state, action) => {
      state.selectedCountriesForBar = action.payload
    },
    setIndicatorMinYear: (state, action) => {
      state.indicatorMinYear = action.payload
    },
    setIndicatorMaxYear: (state, action) => {
      state.indicatorMaxYear = action.payload
    },
    setSelectedMinYear: (state, action) => {
      state.selectedMinYear = action.payload
    },
    setSelectedMaxYear: (state, action) => {
      state.selectedMaxYear = action.payload
    },
    setRecentYearSelection: (state, action) => {
      state.recentYearSelection = action.payload
    },
    setSelectedSingleYear: (state, action) => {
      state.selectedSingleYear = action.payload
    },
    setIndicatorData: (state, action) => {
      state.indicatorData = action.payload
    },
    setSelectedComparison: (state, action) => {
      state.selectedComparison = action.payload
    },
    setFilteredIndicatorData: (state, action) => {
      state.filteredIndicatorData = action.payload
    },
    setDefaultFrequency: (state, action) => {
      state.defaultFrequency = action.payload
    },
    setDefaultUnitMeasure: (state, action) => {
      state.defaultUnitMeasure = action.payload
    },
    setDefaultSecondUnitMeasure: (state, action) => {
      state.defaultSecondUnitMeasure = action.payload
    },
    setDefaultSecondIndicator: (state, action) => {
      state.defaultSecondIndicator = action.payload
    },
    setSelectedSecondIndicator: (state, action) => {
      state.selectedSecondIndicator = action.payload
    },
    setSecondIndicatorData: (state, action) => {
      state.secondIndicatorData = action.payload
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setIsSecondIndicatorLoading: (state, action) => {
      state.isSecondIndicatorLoading = action.payload
    },
    setDataTableData: (state, action) => {
      state.dataTableData = action.payload
    },
    setSelectedUnitMeasure: (state, action) => {
      state.selectedUnitMeasure = action.payload
    },
    setSelectedSecondUnitMeasure: (state, action) => {
      state.selectedSecondUnitMeasure = action.payload
    },
    setSelectedFrequency: (state, action) => {
      state.selectedFrequency = action.payload
    },
    setSelectedOnlyShow: (state, action) => {
      state.selectedOnlyShow[action.payload.chartType] =
        action.payload.selectedOnlyShow
    },
    setSelectedAverage: (state, action) => {
      state.selectedAverage[action.payload.chartType] = action.payload.average
    },
    setSelectedAverages: (state, action) => {
      state.selectedAverages = action.payload
    },
    setAggregateData: (state, action) => {
      state.aggregateData = action.payload
    },
    setFilteredAggregateData: (state, action) => {
      state.filteredAggregateData = action.payload
    },
    setIsTrendPopupShown: (state, action) => {
      state.isTrendPopupShown = action.payload
    },
    setIsAggregateDataLoading: (state, action) => {
      state.isAggregateDataLoading = action.payload
    },
    setUnitMeasureName: (state, action) => {
      state.unitMeasureName = action.payload
    },
    setIndicatorProfileCountryHierarchy: (state, action) => {
      state.countryHierarchy = action.payload
    },
    setIndicatorSpecificRegions: (state, action) => {
      state.indicatorSpecificRegions = action.payload
    },
    setDimensionsData: (state, action) => {
      state.dimensionsData = action?.payload?.availableFilters
      if (action?.payload?.changedFilter !== undefined) {
        state.changedFilterName = action?.payload?.changedFilter
      }
    },
    setIsDimensionsLoading: (state, action) => {
      state.isDimensionsLoading = action.payload
    },
    setSelectedDimensionsList: (state, action) => {
      const { filterName, filterValue } = action.payload

      const existingIndex = state.selectedDimensionsList.findIndex(
        (item) => item.filterColumn === filterName
      )

      if (existingIndex !== -1) {
        state.selectedDimensionsList[existingIndex].filterValue = filterValue
      } else {
        state.selectedDimensionsList.push({
          filterColumn: filterName,
          filterValue: filterValue,
        })
      }
    },
    clearSelectedDimensionsList: (state) => {
      state.selectedDimensionsList = []
    },
    replaceSelectedDimensionsList: (state, action) => {
      state.selectedDimensionsList = action.payload
    },
    removeFromSelectedDimensionsList: (state, action) => {
      const filterName = action.payload
      state.selectedDimensionsList = state.selectedDimensionsList.filter(
        (item) => item.filterColumn !== filterName
      )
    },
    setRefGroupings: (state, action) => {
      state.refGroupings = action.payload
    },
    setBoundaryType: (state, action) => {
      state.boundaryType = action.payload
    },
    setAvailableBoundaryTypes: (state, action) => {
      state.availableBoundaryTypes = action.payload
    },
    setBoundaries: (state, action) => {
      const { boundaryType, data } = action.payload
      state[`${boundaryType}Boundaries`] = data
    },
  },
})

export const {
  setIndicatorCode,
  setDefaultCountryCode,
  setIndicatorMetadata,
  setIsIndicatorMetadataCompleted,
  setDisaggregationData,
  setSecondDisaggregationData,
  setInitialFilters,
  setSelectedFilters,
  resetSelectedCountries,
  setSelectedCountries,
  setSelectedCountriesForBar,
  setIndicatorMinYear,
  setIndicatorMaxYear,
  setSelectedMinYear,
  setSelectedMaxYear,
  setRecentYearSelection,
  setSelectedSingleYear,
  setIndicatorData,
  setFilteredIndicatorData,
  setSelectedComparison,
  setDefaultFrequency,
  setDefaultUnitMeasure,
  setDefaultSecondUnitMeasure,
  setDefaultSecondIndicator,
  setSelectedSecondIndicator,
  setSecondIndicatorData,
  setIsLoading,
  setDataTableData,
  setSelectedUnitMeasure,
  setSelectedSecondUnitMeasure,
  setSelectedFrequency,
  setSelectedOnlyShow,
  setSelectedAverage,
  setSelectedAverages,
  setAggregateData,
  setFilteredAggregateData,
  setIsTrendPopupShown,
  resetSelectedAverage,
  seIsSelectedFiltersUpdated,
  setIsSecondIndicatorLoading,
  setIsAggregateDataLoading,
  setUnitMeasureName,
  setIndicatorProfileCountryHierarchy,
  setIndicatorSpecificRegions,
  setDimensionsData,
  setIsDimensionsLoading,
  setSelectedDimensionsList,
  clearSelectedDimensionsList,
  replaceSelectedDimensionsList,
  removeFromSelectedDimensionsList,
  setRefGroupings,
  setBoundaryType,
  setAvailableBoundaryTypes,
  setBoundaries,
} = indicatorProfileSlice.actions

export default indicatorProfileSlice.reducer
